import React, { useState, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import Tooltip, { TooltipConstants } from '@Components/Tooltip';
import PropTypes from 'prop-types';
import useAccessibleHoverTooltip from '@Components/HoverTooltip/useAccessibleHoverTooltip';
import HoverTooltip from '@Components/HoverTooltip';
import { ButtonWithIcon } from '@els/els-react--button';

import './SvgUseAsButton.scss';

// Workaround for Firefox bug with Flyout placed inside foreignObject - flyout content gets clipped
const overrideTransformModifier = (visible) => ({
  name: 'overrideTransform',
  enabled: true,
  phase: 'write',
  fn({ state }) {
    // The trick is to change position based on visibility
    /* eslint-disable no-param-reassign */
    state.styles.popper.bottom = visible ? '2rem' : '0px';
    state.styles.popper.left = visible ? '1rem' : '0px';
    state.styles.popper.top = 'auto';
    state.styles.popper.right = 'auto';
    /* eslint-enable no-param-reassign */

    Object.assign(state.elements.popper.style, state.styles.popper);
  },
});

export default function SvgUseAsButton(props) {
  const {
    x,
    y,
    href,
    sprite,
    useButton,
    width,
    height,
    onClick,
    className,
    tabIndex,
    children,
    wrapperClassName,
    title,
    tooltipId,
    tooltip,
    ariaLabel,
    ariaDescribedby,
  } = props;
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipRef, setTooltipRef] = useState(null);
  const popperModifiers = useMemo(
    () => [
      // Falls back to top/left instead of transform
      {
        name: 'computeStyles',
        options: { gpuAcceleration: false },
      },
      overrideTransformModifier(tooltipVisible),
    ],
    [tooltipVisible]
  );

  useAccessibleHoverTooltip(tooltipRef, !href);

  useEffect(() => {
    Tooltip.rebuild();
  }, []);

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && onClick) {
      e.preventDefault();
      onClick(e);
    }
  };

  return (
    <foreignObject
      width={width}
      height={height}
      className="svg-use-as-button--foreign-object"
      key={tooltipId || TooltipConstants.ids.TreeViewToolbar}
      y={y}
      x={x}>
      <div xmlns="http://www.w3.org/1999/xhtml" className="svg-hover-tooltip-wrapper">
        <HoverTooltip
          tag="div"
          theme="simple"
          tooltipPlace="top"
          shouldCloseOnESC
          classnames={classnames(['els-btn-link els-btn-lg info-tooltip', className])}
          tooltip={tooltip}
          modifiers={popperModifiers}
          onToggle={setTooltipVisible}>
          <svg width={width} height={height} style={{ overflow: 'visible' }} onClick={onClick}>
            <g
              ref={(ref) => {
                setTooltipRef(ref);
              }}
              tabIndex={href || children ? tabIndex : -1}
              role={href || children ? 'button' : undefined}
              aria-label={href || children ? ariaLabel || title : undefined}
              onKeyDown={handleKeyDown}
              className={classnames('svg-use-as-button--button', wrapperClassName)}>
              <rect fill="transparent" height={height} width={width} y={0} x={0} />
              {title && <title>{title}</title>}
              {href && <use className={className} href={href} height={height} width={width} y={0} x={0} />}
              {sprite && useButton && (
                <foreignObject height={100} width={width} y={0} x={0}>
                  <ButtonWithIcon
                    sprite={sprite}
                    iconOnly
                    type="borderless"
                    onClick={onClick}
                    className={className}
                    aria-label={ariaLabel || title || tooltip}
                    {...(ariaDescribedby && {
                      'aria-describedby': ariaDescribedby,
                    })}
                    data-testid="svg-button-with-icon"
                    data-e2e="svg-button-with-icon">
                    {sprite}
                  </ButtonWithIcon>
                </foreignObject>
              )}
              {children}
            </g>
          </svg>
        </HoverTooltip>
      </div>
    </foreignObject>
  );
}

SvgUseAsButton.propTypes = {
  tooltipId: PropTypes.string,
  tooltip: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  sprite: PropTypes.string,
  useButton: PropTypes.bool,
  href: PropTypes.string,
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  onClick: PropTypes.func,
  wrapperClassName: PropTypes.string,
  title: PropTypes.string,
  ariaLabel: PropTypes.string,
  ariaDescribedby: PropTypes.string,
};
